/**
 * AI-generated code, moderated by lzygmanski
 */
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import TableNotFound from '@/components/data-table/TableNotFound';
import TablePagination from '@/components/data-table/TablePagination';
import TableSearch from '@/components/data-table/TableSearch';
import TableSort from '@/components/data-table/TableSort';
import TableView from '@/components/data-table/TableView';
import {useTable} from '@/components/data-table/useTable';
import type {HealthLogMetricDefinition} from '@/services/graphqlHooks';

import type {
  MeasurementValuesMap,
  ResidentMeasurementRow,
  ResidentWithRoom,
} from '../types/healthLogMeasurementsDialog';
import {buildHealthLogMeasurementsColumns} from '../utils/healthLogMeasurementsColumns';
import {buildTableData} from '../utils/healthLogMeasurementsDialogUtils';

type HealthLogMeasurementsTableProps = {
  residents: ResidentWithRoom[];
  measurementValues: MeasurementValuesMap;
  selectedMetric: HealthLogMetricDefinition | undefined;
  onValueChange: (residentId: string, fieldName: string, value: string) => void;
};

function HealthLogMeasurementsTable({
  residents,
  measurementValues,
  selectedMetric,
  onValueChange,
}: HealthLogMeasurementsTableProps) {
  const {t} = useTranslation();

  // Build table data
  const tableData = useMemo<ResidentMeasurementRow[]>(
    () => buildTableData(residents, measurementValues),
    [residents, measurementValues]
  );

  // Build columns
  const columns = useMemo(
    () =>
      buildHealthLogMeasurementsColumns({
        t,
        selectedMetric,
        onValueChange,
      }),
    [t, selectedMetric, onValueChange]
  );

  // Initialize table
  const table = useTable({
    columns,
    data: tableData,
    initialState: {
      sorting: [{id: 'roomNumber', desc: false}], // Default sort by room
      pagination: {
        pageSize: 50,
      },
      columnVisibility: {
        personalNumber: false, // Hide personal number column
      },
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <TableSearch
          table={table}
          placeholder={t('Resident name, personal number or room number')}
        />
        <TableSort table={table} />
      </div>
      <div className="overflow-auto">
        <TableView table={table} />
      </div>
      <TableNotFound table={table} />
      <TablePagination table={table} />
    </div>
  );
}

export default HealthLogMeasurementsTable;
